import React, { useState, useRef } from 'react';
import { FiUploadCloud, FiImage, FiTrash2, FiRefreshCw, FiCheckCircle } from 'react-icons/fi';
import { fileUploadApi } from '../../api/phpApi';

export default function ImageUploadField({
  label = 'Image',
  value = '',
  onChange,
  folder = 'images',
  helpText = 'Glissez une image ici ou cliquez pour importer (JPG, PNG, WebP, SVG)',
  aspectRatio = 'auto',
  height = 140
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;

    // Validate type
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner un fichier image valide (JPG, PNG, WebP, SVG)');
      return;
    }

    // Validate size (max 8MB)
    if (file.size > 8 * 1024 * 1024) {
      setError('La taille du fichier ne doit pas dépasser 8 Mo');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const result = await fileUploadApi.upload(file);
      if (result && result.url) {
        onChange(result.url);
      } else {
        setError("Erreur lors de l'upload du fichier");
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError("Échec de l'upload: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{ marginBottom: 18 }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontWeight: 600,
            fontSize: 13,
            color: '#1e293b',
            marginBottom: 6
          }}
        >
          {label}
        </label>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      {value ? (
        <div
          style={{
            position: 'relative',
            border: '2px solid #e2e8f0',
            borderRadius: 8,
            padding: 8,
            background: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            gap: 16
          }}
        >
          <div
            style={{
              width: 100,
              height: 80,
              borderRadius: 6,
              overflow: 'hidden',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <img
              src={value}
              alt="Aperçu"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: '#059669',
                fontSize: 13,
                fontWeight: 600
              }}
            >
              <FiCheckCircle size={15} /> Image chargée
            </div>
            <p
              style={{
                margin: '4px 0 0 0',
                fontSize: 11,
                color: '#64748b',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={value}
            >
              {value.startsWith('data:') ? 'Image importée (local)' : value}
            </p>

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '5px 12px',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5
                }}
              >
                <FiRefreshCw size={12} className={uploading ? 'spin' : ''} />
                {uploading ? 'Envoi...' : 'Remplacer'}
              </button>

              <button
                type="button"
                onClick={handleRemove}
                disabled={uploading}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '5px 12px',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5
                }}
              >
                <FiTrash2 size={12} /> Supprimer
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{
            border: `2px dashed ${isDragging ? '#2563eb' : '#cbd5e1'}`,
            borderRadius: 8,
            padding: '24px 16px',
            textAlign: 'center',
            cursor: 'pointer',
            background: isDragging ? '#eff6ff' : '#f8fafc',
            transition: 'all 0.2s ease'
          }}
        >
          {uploading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <FiRefreshCw size={28} color="#2563eb" className="spin" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb' }}>
                Téléversement en cours...
              </span>
            </div>
          ) : (
            <>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: '#e0e7ff',
                  color: '#3730a3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px auto'
                }}
              >
                <FiUploadCloud size={22} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                Importer une image (cliquer ou glisser)
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                {helpText}
              </div>
            </>
          )}
        </div>
      )}

      {error && (
        <div style={{ color: '#dc2626', fontSize: 12, marginTop: 5, fontWeight: 500 }}>
          {error}
        </div>
      )}
    </div>
  );
}
